import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const services = [
  {
    icon: "ri-code-box-line",
    title: "Custom Software Development",
    description:
      "We build tailored software solutions from the ground up — web applications, mobile apps, enterprise systems, and SaaS platforms designed to fit your exact business needs. Our team covers the full stack: frontend, backend, APIs, and database architecture.",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "IT Consulting",
    description:
      "Our consultants help businesses make informed technology decisions, optimize processes, and implement modern tools that drive growth and operational efficiency. From digital transformation strategy to infrastructure audits, we provide actionable insights.",
  },
  {
    icon: "ri-bar-chart-box-line",
    title: "Digital Marketing & Advertising",
    description:
      "We develop comprehensive promotion strategies, strengthen brand presence, and help products find their audience. Our marketing services include SEO, PPC, social media management, content marketing, and performance analytics — all backed by data.",
  },
  {
    icon: "ri-shield-check-line",
    title: "QA & Testing",
    description:
      "Quality is non-negotiable. Our QA engineers provide manual and automated testing services — functional, regression, performance, and security testing — ensuring your product is robust, reliable, and ready for market.",
  },
  {
    icon: "ri-paint-brush-line",
    title: "UI/UX Design",
    description:
      "Great software needs great design. Our UI/UX team creates intuitive, beautiful interfaces that users love. From wireframing and prototyping to full design systems, we craft experiences that convert and delight.",
  },
  {
    icon: "ri-cloud-line",
    title: "Cloud & DevOps",
    description:
      "We help you deploy, scale, and manage your applications in the cloud. Our DevOps services include CI/CD pipelines, containerization, infrastructure as code, and cloud architecture on AWS, Azure, and Google Cloud.",
  },
];

export default function ServicesPage() {
  return (
    <main className="min-h-screen bg-[#0d0d0d]">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20technology%20background%20with%20glowing%20interconnected%20nodes%20and%20digital%20data%20streams%20flowing%20across%20dark%20space%2C%20warm%20copper%20and%20amber%20light%20accents%2C%20futuristic%20digital%20infrastructure%20visualization%2C%20professional%20tech%20render%2C%20dark%20moody%20atmosphere%2C%20clean%20minimal%20design&width=1400&height=700&seq=801&orientation=landscape"
            alt="Trevionix Services"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/60 to-[#0d0d0d]/30" />
        </div>
        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 pb-16 md:pb-24">
          <p
            className="text-[11px] uppercase tracking-[0.28em] text-[#B8422E] mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            / What We Do
          </p>
          <h1
            className="text-4xl md:text-5xl lg:text-[64px] font-extrabold text-white leading-[1.04] tracking-[-0.025em] max-w-2xl"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            Comprehensive
            <br />
            <span className="text-[#6C7278] font-light">Digital Solutions</span>
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="w-full px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          <p
            className="text-base md:text-lg text-white/55 leading-relaxed"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            We are an IT company specializing in software development for businesses and private users, as well as comprehensive turnkey digital solutions. Our expertise covers custom software development, IT consulting, digital marketing and advertising, UI/UX design, QA and testing, and cloud infrastructure.
          </p>
          <p
            className="mt-5 text-base md:text-lg text-white/55 leading-relaxed"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            We accompany projects at all stages — from idea and analytics to development, launch, and ongoing support. We value speed, transparency, and efficiency — you always understand how your project is progressing and what results it brings.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="w-full px-6 md:px-10 lg:px-16 pb-20 md:pb-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]">
          {services.map((s) => (
            <div key={s.title} className="bg-[#0d0d0d] p-8 md:p-10 group hover:bg-[#111213] transition-colors duration-300">
              <div className="w-11 h-11 flex items-center justify-center border border-white/[0.08] mb-6" style={{ borderRadius: "4px" }}>
                <i className={`${s.icon} text-lg text-[#B8422E]`} />
              </div>
              <h3
                className="text-lg font-bold text-white tracking-[-0.01em] mb-3"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                {s.title}
              </h3>
              <p
                className="text-sm text-[#6C7278] leading-relaxed"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                {s.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full px-6 md:px-10 lg:px-16 pb-20 md:pb-28">
        <div className="bg-[#1A1C1E] border border-white/[0.06] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8" style={{ borderRadius: "8px" }}>
          <div>
            <h3
              className="text-2xl md:text-3xl font-extrabold text-white tracking-[-0.02em]"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              Ready to Start Your Project?
            </h3>
            <p
              className="mt-3 text-sm text-[#6C7278] max-w-md"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              Get in touch and let us help you build something great. We respond within one business day.
            </p>
          </div>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-[#B8422E] text-white px-7 py-3 text-[13px] font-semibold hover:bg-[#9E3826] transition-colors duration-300 whitespace-nowrap"
            style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
          >
            Contact Us
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}